import { motion } from "framer-motion";
import { X, Plus, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/constants";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const IngredientsScreen = ({
  ingredients,
  categorizedIngredients,
  categorizedWithQty,
  uploadedImages,
  safetyMessage,
  newIngredient,
  setNewIngredient,
  newIngredientCategory,
  setNewIngredientCategory,
  bulkInput,
  setBulkInput,
  addIngredient,
  addBulkIngredients,
  removeIngredient,
  removeIngredientFromCategory,
  openEditQuantity,
  goBackToPhotos,
  goToVibe,
  validatingIngredient,
}) => {
  return (
    <motion.div
      key="ingredients"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen flex flex-col p-6"
      data-testid="ingredients-screen"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <button 
          onClick={goBackToPhotos}
          className="w-10 h-10 rounded-full bg-white flex items-center justify-center border-2 border-[#264653]/10"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5 text-[#264653]" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-[#264653]">Your Ingredients</h2>
          <p className="text-[#264653]/60 text-sm">Edit or add more below</p>
        </div>
      </div>

      {/* Uploaded Images Preview */}
      {uploadedImages.length > 0 && (
        <div className="mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {uploadedImages.map((img, index) => (
              <img 
                key={index}
                src={img.preview} 
                alt={`Upload ${index + 1}`} 
                className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border-2 border-[#264653]/10"
                data-testid={`uploaded-image-preview-${index}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Safety Message */}
      {safetyMessage && (
        <div className="bg-[#2A9D8F]/10 border border-[#2A9D8F]/20 rounded-2xl p-4 mb-4">
          <p className="text-[#2A9D8F] text-sm">{safetyMessage}</p>
        </div>
      )}

      {/* Categorized Ingredients List */}
      <div className="flex-1 overflow-auto">
        {categorizedWithQty ? (
          <div className="space-y-4 mb-6">
            {CATEGORIES.map(({ key, label, emoji }) => {
              const categoryIngredients = categorizedWithQty[key] || [];
              if (categoryIngredients.length === 0) return null;
              
              return (
                <div key={key} className="bg-white/50 rounded-2xl p-3">
                  <h3 className="text-sm font-semibold text-[#264653]/70 mb-2 flex items-center gap-2">
                    <span>{emoji}</span>
                    {label}
                    <span className="text-xs font-normal text-[#264653]/50">({categoryIngredients.length})</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {categoryIngredients.map((item, index) => (
                      <motion.span
                        key={`${key}-${index}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="ingredient-tag flex items-center gap-1.5"
                        data-testid={`ingredient-tag-${key}-${index}`}
                      >
                        <button
                          onClick={() => openEditQuantity(item.name, key)}
                          className="flex items-center gap-1 hover:opacity-70 transition-opacity"
                          title="Click to edit quantity"
                          data-testid={`edit-qty-${key}-${index}`}
                        >
                          {item.quantity ? (
                            <span className="font-bold text-[#2A9D8F] bg-[#2A9D8F]/10 px-1.5 py-0.5 rounded-md text-xs">
                              {item.quantity}{item.unit && item.unit !== 'pieces' ? ` ${item.unit}` : ''}
                            </span>
                          ) : (
                            <span className="text-[#264653]/40 bg-[#264653]/5 px-1.5 py-0.5 rounded-md text-xs border border-dashed border-[#264653]/20">
                              + qty
                            </span>
                          )}
                          <span className="capitalize">{item.name}</span>
                        </button>
                        <button
                          onClick={() => removeIngredientFromCategory(key, item.name)}
                          className="w-5 h-5 rounded-full bg-[#264653]/10 flex items-center justify-center hover:bg-[#E76F51]/20"
                          data-testid={`remove-ingredient-${key}-${index}`}
                        >
                          <X className="w-3 h-3 text-[#264653]" />
                        </button>
                      </motion.span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : categorizedIngredients ? (
          <div className="space-y-4 mb-6">
            {CATEGORIES.map(({ key, label, emoji }) => {
              const categoryIngredients = categorizedIngredients[key] || [];
              if (categoryIngredients.length === 0) return null;
              
              return (
                <div key={key} className="bg-white/50 rounded-2xl p-3">
                  <h3 className="text-sm font-semibold text-[#264653]/70 mb-2 flex items-center gap-2">
                    <span>{emoji}</span>
                    {label}
                    <span className="text-xs font-normal text-[#264653]/50">({categoryIngredients.length})</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {categoryIngredients.map((ingredient, index) => (
                      <motion.span
                        key={`${key}-${index}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="ingredient-tag"
                        data-testid={`ingredient-tag-${key}-${index}`}
                      >
                        {ingredient}
                        <button
                          onClick={() => removeIngredientFromCategory(key, ingredient)}
                          className="w-5 h-5 rounded-full bg-[#264653]/10 flex items-center justify-center hover:bg-[#E76F51]/20"
                          data-testid={`remove-ingredient-${key}-${index}`}
                        >
                          <X className="w-3 h-3 text-[#264653]" />
                        </button>
                      </motion.span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mb-6">
            {ingredients.map((ingredient, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="ingredient-tag"
                data-testid={`ingredient-tag-${index}`}
              >
                {ingredient}
                <button
                  onClick={() => removeIngredient(index)}
                  className="w-5 h-5 rounded-full bg-[#264653]/10 flex items-center justify-center hover:bg-[#E76F51]/20"
                  data-testid={`remove-ingredient-${index}`}
                >
                  <X className="w-3 h-3 text-[#264653]" />
                </button>
              </motion.span>
            ))}
          </div>
        )}

        {/* Add Ingredient */}
        <div className="space-y-4 mb-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#264653]/70">Add multiple ingredients (comma-separated):</label>
            <div className="flex gap-2">
              <Input
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addBulkIngredients()}
                placeholder="cheese, chicken, tomatoes, rice..."
                className="flex-1 input-field"
                data-testid="bulk-ingredient-input"
              />
              <Button
                onClick={addBulkIngredients}
                disabled={validatingIngredient}
                className="px-4 h-12 rounded-xl bg-[#2A9D8F] text-white text-sm font-medium"
                data-testid="bulk-add-btn"
              >
                {validatingIngredient ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add All"}
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#264653]/20"></div>
            <span className="text-[#264653]/50 text-xs font-medium">or add one at a time</span>
            <div className="flex-1 h-px bg-[#264653]/20"></div>
          </div>
          
          <div className="flex gap-2">
            {categorizedIngredients && (
              <select
                value={newIngredientCategory}
                onChange={(e) => setNewIngredientCategory(e.target.value)}
                className="px-3 py-2 rounded-xl border-2 border-[#264653]/10 bg-white text-sm text-[#264653] focus:border-[#FFB703] focus:outline-none"
                data-testid="ingredient-category-select"
              >
                {CATEGORIES.map(({ key, label, emoji }) => (
                  <option key={key} value={key}>{emoji} {label}</option>
                ))}
              </select>
            )}
            <Input
              value={newIngredient}
              onChange={(e) => setNewIngredient(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && addIngredient()}
              placeholder="Add ingredient..."
              className="flex-1 input-field"
              data-testid="add-ingredient-input"
            />
            <Button
              onClick={addIngredient}
              disabled={validatingIngredient}
              className="w-12 h-12 rounded-xl bg-[#2A9D8F] text-white p-0"
              data-testid="add-ingredient-btn"
            >
              {validatingIngredient ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Continue Button */}
      <button
        onClick={goToVibe}
        disabled={ingredients.length === 0}
        className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="continue-to-vibe-btn"
      >
        Choose Your Vibe
        <ChevronRight className="w-5 h-5" />
      </button>
    </motion.div>
  );
};
